import { useNavigate, useParams } from '@tanstack/react-router';
import { RefreshCcw, SquarePen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  DataTableActionBar,
  DataTableActionBarAction,
  DataTableActionBarSelection,
} from '@/components/data-table/data-table-action-bar';
import { Button } from '@/components/ui/button';
import { usePurchaseOrderProductsTable } from '@/features/purchase-order/hooks/use-purchase-order-products-table';
import { usePurchaseOrderSyncInformationsButton } from '@/features/purchase-order/hooks/use-purchase-order-sync-informations-button';
import { useGetPurchaseOrder } from '@/services/apis/queries/purchase-order/use-get-purchase-order';
import { PurchaseOrderProductsSyncInformationsButton as SyncInformationsButton } from './purchase-order-products-sync-informations-button';
import { PurchaseOrderProductsTable } from './purchase-order-products-table';

export function PurchaseOrderProducts() {
  const navigate = useNavigate();
  const { id } = useParams({ strict: false });
  const { t } = useTranslation('purchaseOrder');
  const purchaseOrderId = id ?? '';
  const { data: purchaseOrder } = useGetPurchaseOrder(purchaseOrderId);
  const lotSizingId = purchaseOrder?.lotSizingId;

  const { table, totalProductsCount, isPending } =
    usePurchaseOrderProductsTable(purchaseOrderId);

  const syncInformations = usePurchaseOrderSyncInformationsButton({
    purchaseOrderId,
    table,
    totalProductsCount,
  });

  return (
    <div className='flex min-h-0 flex-1 flex-col gap-2'>
      <div className='flex justify-end gap-2'>
        <SyncInformationsButton {...syncInformations} />
        <Button
          disabled={!(lotSizingId && syncInformations.isSyncEnabled)}
          onClick={() => {
            if (!lotSizingId) {
              return;
            }

            navigate({ to: '/lot-sizing/$id', params: { id: lotSizingId } });
          }}
          type='button'
        >
          {t('edit', { ns: 'common' })}
          <SquarePen aria-hidden className='size-4' />
        </Button>
      </div>

      <div className='min-w-0 flex-1'>
        <PurchaseOrderProductsTable
          actionBar={
            <DataTableActionBar table={table}>
              <DataTableActionBarSelection table={table} />
              <DataTableActionBarAction
                disabled={!syncInformations.isSyncEnabled}
                onClick={syncInformations.data.dialog.onOpen}
                type='button'
              >
                <RefreshCcw className='size-4' />
                {t('syncInformationsSelectedBtn', {
                  count: syncInformations.data.counts.selectedProductsCount,
                })}
              </DataTableActionBarAction>
            </DataTableActionBar>
          }
          isPending={isPending}
          table={table}
        />
      </div>
    </div>
  );
}
